/**
 * ExpiringDocumentsWidget — small dashboard card showing the next 5 branch
 * documents (permits / licenses / accreditation) within their 30-day expiration
 * window (or already expired). Clicking a row deep-links to the Documents page
 * with the relevant branch preselected.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FileText, AlertTriangle, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";

const STATUS_BADGES = {
  valid: { label: "Valid", cls: "bg-[#DCFCE7] text-[#166534]", Icon: CheckCircle2 },
  expiring_soon: { label: "Soon", cls: "bg-[#FEF3C7] text-[#92400E]", Icon: AlertTriangle },
  expired: { label: "Expired", cls: "bg-[#FEE2E2] text-[#991B1B]", Icon: AlertCircle },
};

export default function ExpiringDocumentsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/branch-documents/expiring", { params: { days: 30, limit: 5 } });
        if (!cancelled) setItems(data?.items || []);
      } catch {
        // 403 (no branch_documents_view perm) is fine — just hide the widget.
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (errored || (!loading && items.length === 0)) return null;

  return (
    <section
      className="bg-white rounded-2xl border border-[#2563EB]/10 shadow-floating p-5"
      data-testid="dash-expiring-documents"
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-2xl bg-[#FEE2E2] flex items-center justify-center">
            <FileText className="w-4 h-4 text-[#B91C1C]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-foreground tracking-tight">Expiring Documents</h3>
            <p className="text-xs text-slate-500">Next 30 days · top 5</p>
          </div>
        </div>
        <Link
          to="/documents"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline"
          data-testid="dash-expiring-documents-view-all"
        >
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </header>

      {loading ? (
        <div className="text-xs text-slate-500 text-center py-4">Loading…</div>
      ) : (
        <ul className="divide-y divide-[#2563EB]/10">
          {items.map((row) => {
            const badge = STATUS_BADGES[row.status] || STATUS_BADGES.valid;
            const Icon = badge.Icon;
            const days = row.days_until_expiration;
            const dayLabel = days < 0
              ? `${Math.abs(days)}d overdue`
              : (days === 0 ? "expires today" : `in ${days}d`);
            return (
              <li key={row.id}>
                <Link
                  to={`/documents?branch=${row.branch_id}`}
                  className="flex items-center gap-3 py-2.5 hover:bg-brand-purpleLight/30 -mx-2 px-2 rounded-xl transition-colors"
                  data-testid={`dash-expiring-doc-row-${row.id}`}
                >
                  <div className="w-9 h-9 rounded-2xl bg-[#EFF6FF] flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-[#2563EB]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-foreground truncate">{row.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {row.branch_name} · expires {row.expiration_date}
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${badge.cls}`}>
                    <Icon className="w-3 h-3" /> {dayLabel}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
